import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import type { FormErrors } from '@jwp/ott-common/types/form';
import type { Offer, ChooseOfferFormData, AvailableOffer } from '@jwp/ott-common/types/checkout';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { getAvailableOfferPrice, getOfferPrice, isSVODOffer } from '@jwp/ott-common/src/utils/offers';
import { testId } from '@jwp/ott-common/src/utils/common';
import CheckRed from '@jwp/ott-theme/assets/icons/check_red.svg?react';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import { useCheckoutStore } from '@jwp/ott-common/src/stores/CheckoutStore';

import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';
import DialogBackButton from '../DialogBackButton/DialogBackButton';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';

import styles from './UpdateSubscriptionForm.module.scss';
import { useTheme } from '../../../../../ThemeContext';
import { modalURLFromLocation } from '../../utils/location';
import { getModule } from '@jwp/ott-common/src/modules/container';
import CheckoutController from '@jwp/ott-common/src/controllers/CheckoutController';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { formatAmount } from '@jwp/ott-common/src/utils/formatting';

type OfferBoxProps = {
  offer: AvailableOffer;
  selected: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const OfferBox: React.FC<OfferBoxProps> = ({ offer, selected, onChange }: OfferBoxProps) => {
  const { t } = useTranslation('account');
  const { universalStyles } = useTheme();
  const { pendingOffer } = useAccountStore();
  // const getFreeTrialText = (offer: Offer) => {
  //   if (offer.freeDays) {
  //     return t('choose_offer.benefits.first_days_free', { count: offer.freeDays });
  //   } else if (offer.freePeriods) {
  //     // t('periods.day', { count })
  //     // t('periods.week', { count })
  //     // t('periods.month', { count })
  //     // t('periods.year', { count })
  //     const period = t(`periods.${offer.period}`, { count: offer.freePeriods });

  //     return t('choose_offer.benefits.first_periods_free', { count: offer.freePeriods, period });
  //   }

  //   return null;
  // };

  const renderOption = ({ title, periodString, secondBenefit }: { title: string; periodString?: string; secondBenefit?: string }) => (
    <div className={styles.offer} role="option">
      <input
        className={styles.radio}
        onChange={onChange}
        type="radio"
        name={'selectedOfferId'}
        value={offer.toOfferId}
        id={offer.toOfferId}
        checked={selected}
        disabled={!offer.toOfferId || offer.toOfferId == pendingOffer?.offerId}
        data-testid={testId(offer.toOfferId)}
      />
      <label className={styles.label} htmlFor={offer.toOfferId}>
        <span className={styles.offerTitle}>
          <h2>{title}</h2>
          {!offer.toOfferId && (
            <p className={styles.currentPlan} style={{ color: universalStyles.successColor.color }}>
              {t('checkout.current_plan')}
            </p>
          )}
          {offer.toOfferId == pendingOffer?.offerId && (
            <p className={styles.currentPlan} style={{ color: universalStyles.successColor.color }}>
              {t('checkout.upcoming_plan')}
            </p>
          )}
        </span>

        {/* <hr className={styles.offerDivider} /> */}
        <div className={styles.offerPrice}>
          {/* {getOfferPrice(offer)} {!!periodString && <small>/{periodString}</small>} */}
          {offer.nextPaymentPriceCurrencySymbol}
          {offer.nextPaymentPrice && formatAmount(offer.nextPaymentPrice)}
        </div>
        <div className={styles.offerTime}>{!!periodString && <small>{t('choose_offer.per')} {periodString}</small>}</div>
        <ul className={styles.offerBenefits}>
          {/* {offer.freeDays || offer.freePeriods ? (
            <li>
              <Icon icon={CheckRed} /> {getFreeTrialText(offer)}
            </li>
          ) : null} */}

          {!!secondBenefit && (
            <li>
              <Icon icon={CheckRed} /> {secondBenefit}
            </li>
          )}
          <li>
            <Icon icon={CheckRed} /> <span>{t('choose_offer.benefits.watch_on_all_devices')}</span>
          </li>
        </ul>
        <div className={styles.fill} />
      </label>
    </div>
  );

  // if (isSVODOffer(offer)) {
  //   const isMonthly = offer.period === 'month';

  //   return renderOption({
  //     title: isMonthly ? t('choose_offer.monthly') : t('choose_offer.yearly'),
  //     secondBenefit: t('choose_offer.benefits.cancel_anytime'),
  //     periodString: isMonthly ? t('periods.month') : t('periods.year'),
  //   });
  // }

  return renderOption({
    title: offer.title,
    secondBenefit: t('choose_offer.benefits.cancel_anytime'),
    periodString: offer.period,
  });
};

const UpdateSubscription = () => {
  const siteName = useConfigStore((s) => s.config.siteName);
  const { t } = useTranslation('account');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<AvailableOffer | undefined>();
  const [loading, setLoading] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const checkoutController = getModule(CheckoutController);
  const accountController = getModule(AccountController);
  function capitalizeFirstLetter(val: string | undefined) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + clientWidth > scrollWidth - 30);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleNext = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth',
      });
    }
  };

  const handlePrevious = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth',
      });
    }
  };

  const convertedSwitchSubscriptionOffers = (data: any) => {
    if (data) {
      const currencySymbols: { [key: string]: string } = {
        USD: '$',
        INR: '₹',
        EUR: '€',
      };

      const currencySymbol = currencySymbols[data.nextPaymentCurrency] || '$';

      return {
        toOfferId: '',
        algorithm: '',
        switchDirection: '',
        title: data.offerTitle,
        price: data.totalPrice,
        currency: data.nextPaymentCurrency,
        currencySymbol: currencySymbol,
        period: data.period,
        nextPaymentPrice: data.totalPrice,
        nextPaymentPriceCurrency: data.nextPaymentCurrency,
        nextPaymentPriceCurrencySymbol: currencySymbol,
      };
    }

    return null;
  };
  const switchSubscriptionOffers = useCheckoutStore((s) => s.availableSwitchesSubscription);
  const activeSubscription = useAccountStore((s) => s.subscription);
  const sortedPackages = [convertedSwitchSubscriptionOffers(activeSubscription), ...(switchSubscriptionOffers || [])]
    .filter((offer): offer is AvailableOffer => offer !== null)
    .sort((a, b) => a.price - b.price);

  const isSingleOrTwoOffers = sortedPackages.length === 1 || sortedPackages.length === 2;
  const handleSuccess = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOfferId) {
      return;
    }
    setLoading(true);
    try {
      const response = await checkoutController.switchSubscription();

      if (response) {
        await accountController.reloadSubscriptions();
        await checkoutController.getSubscriptionSwitches();
        navigate(modalURLFromLocation(location, 'upgrade-subscription-success'));
      }
    } catch (error) {
      console.error('Error switching subscription:', error);
      return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSuccess} data-testid={testId('choose-offer-form')} noValidate>
      <h1 className={styles.title}>{t('choose_offer.change_plan')}</h1>
      <div className={styles.offers} ref={containerRef}>
        {!sortedPackages.length ? (
          <p>{t('choose_offer.no_pricing_available')}</p>
        ) : (
          sortedPackages.map((offer) => (
            <OfferBox
              key={offer?.title}
              offer={offer}
              selected={offer.toOfferId === selectedOfferId?.toOfferId}
              onChange={(e) => {
                const selectedOffer = sortedPackages.find((offer) => offer.toOfferId === e.target.value);
                setSelectedOfferId(selectedOffer);
                useCheckoutStore.setState({ upgradeSelectedOffer: selectedOffer || null });
              }}
            />
          ))
        )}
        {!isSingleOrTwoOffers && !isAtStart && (
          <div className={styles.previousButton} onClick={handlePrevious}>
            <IconButton className={styles.clearButton} aria-label={t('search_bar.clear_search_label')}>
              <Icon icon={ChevronLeft} />
            </IconButton>
          </div>
        )}
        {!isSingleOrTwoOffers && !isAtEnd && (
          <div className={styles.nextButton} onClick={handleNext}>
            <IconButton className={styles.clearButton} aria-label={t('search_bar.clear_search_label')}>
              <Icon icon={ChevronRight} />
            </IconButton>
          </div>
        )}
      </div>
      {loading && <LoadingOverlay transparentBackground inline />}
      <center>
        <Button
          label={selectedOfferId?.switchDirection ? capitalizeFirstLetter(selectedOfferId?.switchDirection) : t('choose_offer.continue')}
          variant="contained"
          color="primary"
          type="submit"
          disabled={!selectedOfferId || loading}
          halfwith={true}
        />
      </center>
    </form>
  );
};
export default UpdateSubscription;
